import React, { useEffect, useRef } from 'react';

/* The tube tracers behind Milli's dashboard (Superdesign draft v10, 2026-09-30).
 *
 * Desk only: the console mounts this component only on a wide screen, and
 * the build plan's decision is that phones keep the static charcoal. The
 * effect is the TubesCursor scene from `threejs-components` (Three.js
 * bundled, 775 KB minified), so it is a lazy chunk that a desk browser
 * downloads once, and a phone never does.
 *
 * Respects prefers-reduced-motion: no canvas, no download. A load or WebGL
 * failure is swallowed: the page is complete without the effect, and the
 * K.O. Rule says nothing on this page may break a reading of the numbers.
 *
 * The ribbons follow the pointer anywhere on the page (the library listens
 * on document.body, so the pointer-events-none wrapper does not matter) and
 * wander on their own when the pointer leaves the window.
 *
 * Colors and brightness (2026-10-25, J.O.'s open space): the defaults are
 * the rulebook metals on charcoal, section charcoal, brushed silver, antique
 * gold for the tubes, copper joining in the lights. The board's side bar can
 * change them; the library exposes setColors and setLightsColors on the
 * running scene, so a change lands without rebuilding it. */

export const DEFAULT_TUBE_COLORS = ['#25272B', '#b8b8b8', '#C9A24B'];
export const DEFAULT_TUBE_OPACITY = 0.4;
const COPPER = '#C08A5E';

const reducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

export default function TubesBackground({ opacity = DEFAULT_TUBE_OPACITY, colors = DEFAULT_TUBE_COLORS }) {
  const canvasRef = useRef(null);
  const handleRef = useRef(null);
  const colorsRef = useRef(colors);
  colorsRef.current = colors;
  const quiet = reducedMotion();

  useEffect(() => {
    if (quiet) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let cancelled = false;
    import('threejs-components/build/cursors/tubes1.min.js')
      .then((mod) => {
        if (cancelled) return;
        const TubesCursor = mod.default;
        const tubeColors = colorsRef.current;
        handleRef.current = TubesCursor(canvas, {
          tubes: {
            colors: tubeColors,
            lights: { intensity: 120, colors: [...tubeColors, COPPER] },
          },
        });
      })
      .catch(() => {
        /* no tubes today; the dashboard stands on its own */
      });
    return () => {
      cancelled = true;
      try {
        handleRef.current?.dispose?.();
      } catch {
        /* already gone */
      }
      handleRef.current = null;
    };
  }, [quiet]);

  // Recolor the running scene when the board changes the ribbons.
  useEffect(() => {
    const scene = handleRef.current?.tubes;
    if (!scene) return;
    try {
      scene.setColors?.(colors);
      scene.setLightsColors?.([...colors, COPPER]);
    } catch {
      /* a bad color never breaks the page */
    }
  }, [colors]);

  if (quiet) return null;
  return (
    <div
      className="pointer-events-none fixed inset-0 z-0 transition-opacity duration-300"
      style={{ opacity }}
      aria-hidden="true"
      data-testid="milli-tubes"
    >
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
